import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { resetPassword } from "@/services/auth";

function ResetCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 w-full max-w-md">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-md">
          SE
        </div>
        {children}
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  // The recovery email link carries the code as `code` (fall back to `token`).
  const code = searchParams.get("code") || searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const isLoading = status === "loading";
  const isSuccess = status === "success";

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setStatus("loading");
    try {
      await resetPassword({ code, password, logoutFromAllDevices: true });
      setStatus("success");
      setTimeout(() => navigate("/", { replace: true }), 3000);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
      setStatus("error");
    }
  }

  if (!code) {
    return (
      <ResetCard>
        <p role="alert" className="text-center text-sm text-red-600">
          Invalid or missing reset link. Please request a new password reset email.
        </p>
      </ResetCard>
    );
  }

  if (isSuccess) {
    return (
      <ResetCard>
        <p role="status" className="text-center text-sm text-gray-700">
          Password updated! Redirecting to sign in…
        </p>
      </ResetCard>
    );
  }

  return (
    <ResetCard>
      <h1 className="text-xl font-bold text-gray-900 text-center">Choose a new password</h1>
      <p className="text-sm text-gray-500 mt-1.5 mb-6 text-center">
        Enter a new password for your SELISE Group account
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              autoFocus
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={8}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <Button type="submit" className="w-full h-12 font-medium" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Updating…
            </>
          ) : (
            "Reset password"
          )}
        </Button>
      </form>
    </ResetCard>
  );
}
